import { Component, Inject, inject, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatButton } from '@angular/material/button'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { TranslatePipe } from '@ngx-translate/core'
import { UserUploadedResource } from '../call-document-upload/call-document-upload.component'
import { CallResource, CallsService } from '../calls.service'
import { FileUploadComponent } from '../file-upload/file-upload.component'
import { SpinnerComponent } from '../spinner/spinner.component'
import { ToastService } from '../toast.service'
import { FileUploadType, getUploadErrorMessage } from '../uploads.service'
import { DocumentUploadInfo, toSourceDocumentFormat } from '../utils'

export type DocumentUploadDialogInputData = {
  callId: number
  title: string
  documentToUpload: UserUploadedResource
  type: FileUploadType
  afterUpload?: (resource: CallResource) => Promise<boolean>
}

@Component({
  selector: 'app-document-upload-dialog',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, MatButton, TranslatePipe, SpinnerComponent, FileUploadComponent],
  templateUrl: './document-upload-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './document-upload-dialog.component.scss',
})
export class DocumentUploadDialogComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DocumentUploadDialogInputData,
    private dialogRef: MatDialogRef<DocumentUploadDialogComponent>
  ) {}

  @ViewChild(FileUploadComponent)
  fileUploadComponent!: FileUploadComponent

  callsService = inject(CallsService)
  toastService = inject(ToastService)
  importResourceMutation = this.callsService.importResource()
  uploading = false
  uploadInfo: DocumentUploadInfo | null = null

  get acceptedExtensions() {
    return this.data.documentToUpload.extensions
  }

  get maxSize() {
    return this.data.documentToUpload.maxSize
  }

  get type() {
    return this.data.documentToUpload.uploadType
  }

  async onUploadClick() {
    this.uploading = true
    try {
      const resource = await this.importResourceMutation.mutateAsync({
        callId: this.data.callId,
        data: {
          fileType: this.data.documentToUpload.type,
          targetResourceExtension: this.uploadInfo!.extension,
          uploadKey: this.uploadInfo!.key,
          fileName: this.uploadInfo!.fileName,
          sourceDocumentFormat: toSourceDocumentFormat(this.uploadInfo!.extension),
        },
      })

      if (this.data.afterUpload !== undefined) {
        let hookCompleted = false

        while (hookCompleted === false) {
          hookCompleted = await this.data.afterUpload!(resource)

          if (!hookCompleted) {
            await new Promise((resolve) => setTimeout(resolve, 500))
          }
        }
      }

      this.uploading = false
      this.uploadInfo = null
      this.dialogRef.close(resource)
    } catch (error) {
      this.uploading = false
      this.uploadInfo = null
      const uploadErrorMessage = getUploadErrorMessage(error)
      if (uploadErrorMessage !== null) {
        this.toastService.show(uploadErrorMessage)
      } else {
        this.toastService.show('CALLS.CALL_INFO.DOCUMENT_UPLOAD.UPLOAD_FAILED')
      }
    }
  }
}
